import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

// Register this interceptor once in AppModule using HTTP_INTERCEPTORS.
// It automatically trims string values in JSON-like POST, PUT, and PATCH bodies,
// so components do not need to manually clean every outgoing API payload.

@Injectable()
export class TrimRequestBodyInterceptor implements HttpInterceptor {
  // Runs before outgoing HTTP requests and clones trimable requests with a normalized body.
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.hasTrimableBody(request)) {
      return next.handle(request);
    }

    return next.handle(
      request.clone({
        body: this.trimValue(request.body)
      })
    );
  }

  // Limits automatic trimming to write requests with JSON-like bodies.
  private hasTrimableBody(request: HttpRequest<any>): boolean {
    return ['POST', 'PUT', 'PATCH'].includes(request.method) && !this.isUntrimableValue(request.body);
  }

  // Recursively trims string values while preserving arrays, objects, numbers, booleans, and nulls.
  private trimValue(value: any): any {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (Array.isArray(value)) {
      return value.map(item => this.trimValue(item));
    }

    if (this.isPlainObject(value)) {
      return Object.keys(value).reduce((result, key) => {
        result[key] = this.trimValue(value[key]);
        return result;
      }, {} as Record<string, any>);
    }

    return value;
  }

  // Detects plain object request bodies so class instances and special browser objects are left untouched.
  private isPlainObject(value: any): value is Record<string, any> {
    return !!value && Object.prototype.toString.call(value) === '[object Object]';
  }

  // Skips values where trimming would be unsafe or meaningless, such as uploads, binary data, dates, and empty bodies.
  private isUntrimableValue(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      value instanceof FormData ||
      value instanceof Blob ||
      value instanceof ArrayBuffer ||
      value instanceof Date ||
      value instanceof URLSearchParams
    );
  }
}
